import { useEffect, useRef } from 'react';
import useSocket from './useSocket';

export default function useSocketSubscription(resource, resourceIds = null, requiredResources = false, fromTimestamp = null, resourceCursors = null) {
  const { socket, connectionState } = useSocket();
  const isConnected = connectionState.status === 'connected';
  const previousResourceIds = useRef(null);

  useEffect(() => {
    if (!isConnected || !resource) return;
    if (requiredResources && resourceIds === null) return;

    const currentResourceIds = resourceCursors
      ? resourceCursors.map(({ id }) => id)
      : resourceIds;

    // If we have previous IDs, unsubscribe from the ones that are no longer present
    if (previousResourceIds.current) {
      const removedIds = previousResourceIds.current.filter(id =>
        !currentResourceIds || !currentResourceIds.includes(id)
      );
      if (removedIds.length > 0) {
        socket.unsubscribeFromResource(resource, removedIds);

        // If we're unsubscribing from all previous IDs, 
        // clear previousResourceIds to prevent future unsubscribe attempts
        if (removedIds.length === previousResourceIds.current.length) {
          previousResourceIds.current = null;
        }
      }
    }

    // Subscribe to new IDs
    if (currentResourceIds) {
      const newIds = currentResourceIds.filter(id =>
        !previousResourceIds.current || !previousResourceIds.current.includes(id)
      );
      if (newIds.length > 0) {
        if (resourceCursors) {
          resourceCursors
            .filter(({ id }) => newIds.includes(id))
            .forEach(({ id, fromTimestamp: cursor }) => {
              socket.subscribeToResource(resource, [id], cursor ?? null);
            });
        } else {
          const fromDt = fromTimestamp ?? null;
          socket.subscribeToResource(resource, newIds, fromDt);
        }
      }
    } else {
      // Resource-level subscription (no specific IDs)
      const fromDt = fromTimestamp ?? null;
      socket.subscribeToResource(resource, null, fromDt);
    }

    // Update previous IDs
    previousResourceIds.current = currentResourceIds;

    // Cleanup: unsubscribe from all IDs when component unmounts
  }, [socket, isConnected, resource, resourceIds, requiredResources, fromTimestamp, resourceCursors]);

  useEffect(() => {
    return () => {
      if (!socket || !resource) return;

      if (previousResourceIds.current) {
        socket.unsubscribeFromResource(resource, previousResourceIds.current);
        // Clear the reference after unsubscribing to prevent future issues
        previousResourceIds.current = null;
      } else {
        socket.unsubscribeFromResource(resource);
      }
    };
  }, [socket, resource]);

  return isConnected;
}
